import { CartAddEvent, CartUpdateEvent } from '@theme/events';

/**
 * Free-sample picker for the cart drawer.
 *
 * Adds/removes $0 "Sample Product" items via Horizon's own cart endpoints, then
 * dispatches the theme's cart:update event so <cart-items-component> morphs the
 * drawer section and re-renders this picker (all state lives in Liquid).
 *
 * The allowance (1 per order + 1 per $20, capped at 5) is computed in the snippet;
 * this script only enforces it on click and trims any excess after a tier drop.
 */

const SAMPLE_PROPERTY = '_sample';

/**
 * Cart AJAX endpoints. Horizon does NOT expose a `window.Theme` global (the prior
 * dev copied `Theme.routes.*` from another theme), so those references resolved to
 * undefined and every add/change POST 404'd to an HTML page — which then failed
 * JSON.parse and silently killed the trim. Build the endpoints from the always-
 * present `Shopify.routes.root` instead (locale-aware, e.g. "/" or "/en-ca/").
 */
const CART_ROOT = () => (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
const cartUrl = () => `${CART_ROOT()}cart.js`;
const cartAddUrl = () => `${CART_ROOT()}cart/add.js`;
const cartChangeUrl = () => `${CART_ROOT()}cart/change.js`;
const cartUpdateUrl = () => `${CART_ROOT()}cart/update.js`;

/** All cart-items-component section ids, so responses include their HTML to morph. */
function cartSectionIds() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component').forEach((component) => {
    if (component instanceof HTMLElement && component.dataset.sectionId) {
      ids.add(component.dataset.sectionId);
    }
  });
  return Array.from(ids).join(',');
}

class SamplePickerComponent extends HTMLElement {
  #busy = false;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  get sectionId() {
    return this.dataset.sectionId || '';
  }

  get allowance() {
    return parseInt(this.dataset.allowance || '0', 10);
  }

  get chosen() {
    return parseInt(this.dataset.chosen || '0', 10);
  }

  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-sp-tile]') : null;
    if (!target || this.#busy || target.hasAttribute('disabled')) return;
    event.preventDefault();

    if (target.classList.contains('is-selected')) {
      this.#removeSample(target.getAttribute('data-line-key'), target);
    } else if (this.chosen < this.allowance) {
      this.#addSample(target.getAttribute('data-variant-id'), target);
    } else {
      this.#flashLimit();
    }
  };

  /**
   * Instant visual feedback: flip the tile + count the moment it's clicked, before
   * the cart round-trip and drawer morph land. The authoritative morph re-renders the
   * picker a beat later and confirms the same state, so there's no flicker. Reverted
   * by the caller if the request fails.
   */
  #optimistic(tile, selected) {
    if (tile) tile.classList.toggle('is-selected', selected);
    const next = Math.max(0, this.chosen + (selected ? 1 : -1));
    this.dataset.chosen = String(next);
    const countEl = this.querySelector('[data-sp-count]');
    if (countEl) countEl.innerHTML = `${next}&thinsp;/&thinsp;${this.allowance}`;
  }

  #setBusy(state) {
    this.#busy = state;
    this.classList.toggle('is-busy', state);
  }

  #flashLimit() {
    const count = this.querySelector('[data-sp-count]');
    count?.animate(
      [{ transform: 'scale(1)' }, { transform: 'scale(1.22)' }, { transform: 'scale(1)' }],
      { duration: 320, easing: 'ease-out' }
    );
  }

  async #addSample(variantId, tile) {
    if (!variantId) return;
    this.#setBusy(true);
    this.#optimistic(tile, true);

    const body = new FormData();
    body.append('id', variantId);
    body.append('quantity', '1');
    body.append(`properties[${SAMPLE_PROPERTY}]`, 'true');
    body.append('sections', cartSectionIds());
    body.append('sections_url', window.location.pathname);

    try {
      const response = await fetch(cartAddUrl(), {
        method: 'POST',
        headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' },
        body,
      });
      const data = await response.json();
      if (data && data.status) {
        this.#optimistic(tile, false); // add failed (e.g. sold out) — undo the flip
        return;
      }

      this.dispatchEvent(
        new CartAddEvent({}, this.sectionId, { source: 'dgt-sample-picker', sections: data.sections })
      );
    } catch (error) {
      this.#optimistic(tile, false);
      console.error('[dgt-sample-picker] add failed', error);
    } finally {
      this.#setBusy(false);
    }
  }

  async #removeSample(lineKey, tile) {
    if (!lineKey) return;
    this.#setBusy(true);
    this.#optimistic(tile, false);

    try {
      const response = await fetch(cartChangeUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'text/html' },
        body: JSON.stringify({
          id: lineKey,
          quantity: 0,
          sections: cartSectionIds(),
          sections_url: window.location.pathname,
        }),
      });
      const data = await response.json();

      this.dispatchEvent(
        new CartUpdateEvent(data, this.sectionId, { source: 'dgt-sample-picker', sections: data.sections })
      );
    } catch (error) {
      this.#optimistic(tile, true); // remove failed — restore the tile
      console.error('[dgt-sample-picker] remove failed', error);
    } finally {
      this.#setBusy(false);
    }
  }

}

if (!customElements.get('sample-picker-component')) {
  customElements.define('sample-picker-component', SamplePickerComponent);
}

/**
 * Allowance = 1 per order + 1 per $20 of PAID total, capped at 6. Samples are $0,
 * so cart.total_price is the paid total. Must match the Liquid formula in
 * snippets/dgt-sample-picker.liquid.
 */
function sampleAllowance(totalCents) {
  return Math.min(5, Math.floor(totalCents / 2000) + 1);
}

/**
 * Enforce the allowance from the AUTHORITATIVE cart (not the picker's DOM state,
 * which can be stale/mistimed after a morph). If removing a paid item drops the
 * allowance below the number of chosen samples, remove the newest sample(s) until
 * the cart is within allowance. Runs after every cart change and converges.
 */
/** A cart JSON is usable as-is only if it carries the line items + paid total. */
function usableCart(cart) {
  return cart && Array.isArray(cart.items) && typeof cart.total_price === 'number' ? cart : null;
}

let trimming = false;
async function enforceSampleAllowance(seedCart) {
  if (trimming) return;
  trimming = true;
  try {
    // Start from the cart the triggering event already carries (no round-trip); only
    // hit /cart.js when we weren't handed a usable one.
    const cart = usableCart(seedCart) || (await (await fetch(cartUrl(), { cache: 'no-store' })).json());
    // Count samples the SAME way the Liquid snippet does — by product type — plus the
    // _sample property, so JS and the displayed count can never disagree (a mismatch
    // here is what let excess samples slip past the trim).
    const isSample = (i) => i.product_type === 'Sample Product' || (i.properties && i.properties[SAMPLE_PROPERTY]);
    const samples = (cart.items || []).filter(isSample);
    const sampleUnits = samples.reduce((n, i) => n + i.quantity, 0);
    const paidUnits = (cart.items || []).reduce((n, i) => (isSample(i) ? n : n + i.quantity), 0);
    // Free samples come WITH an order — if the cart has no paid items, allow zero, so
    // deleting everything but the samples clears them too (not the +1 base allowance).
    const allowance = paidUnits === 0 ? 0 : sampleAllowance(cart.total_price);
    let excess = sampleUnits - allowance;
    if (excess <= 0) return;

    // Trim the newest sample lines down to the allowance — ALL in one /cart/update.js
    // request (multi-line), so N over-limit samples take a single round-trip + a single
    // drawer re-render instead of one per sample.
    const updates = {};
    for (let i = samples.length - 1; i >= 0 && excess > 0; i--) {
      const take = Math.min(samples[i].quantity, excess);
      updates[samples[i].key] = samples[i].quantity - take;
      excess -= take;
    }
    const response = await fetch(cartUpdateUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'text/html' },
      body: JSON.stringify({ updates, sections: cartSectionIds(), sections_url: window.location.pathname }),
    });
    const data = await response.json();
    // Dispatch on `document`, NOT the picker element. When the last paid item is
    // deleted the Liquid stops rendering <sample-picker-component>, so dispatching on
    // the (now-missing) picker was a silent no-op — the samples were removed
    // server-side but the drawer never re-rendered until a manual refresh. document is
    // always present; cart-items-component listens there and re-renders from the
    // response sections.
    document.dispatchEvent(
      new CartUpdateEvent(data, '', { source: 'dgt-sample-picker-trim', sections: data.sections })
    );
  } catch (error) {
    console.error('[dgt-sample-picker] allowance enforcement failed', error);
  } finally {
    trimming = false;
  }
}

document.addEventListener('cart:update', (event) => {
  // Our own trim re-dispatches cart:update; the `trimming` guard makes that re-entry a
  // no-op, so no loop. Run immediately and reuse the cart the event carries so the
  // excess samples vanish in the same render as the removal, not a beat later.
  enforceSampleAllowance(event?.detail?.resource);
});

/**
 * Belt-and-suspenders: some cart mutations (certain remove paths, in-place morphs)
 * don't reliably bubble a 'cart:update' to document, so the excess samples would
 * linger until a manual refresh. Also WATCH the cart drawer directly — whenever the
 * picker re-renders with a new data-chosen/data-allowance and chosen exceeds the
 * (recomputed) allowance, enforce. The `trimming` guard + convergence prevents loops.
 */
function attachSampleObservers() {
  document.querySelectorAll('cart-items-component').forEach((host) => {
    if (host.__dgtSampleObserved) return;
    host.__dgtSampleObserved = true;
    new MutationObserver(() => {
      const sp = host.querySelector('sample-picker-component');
      if (!sp) return;
      const chosen = parseInt(sp.dataset.chosen || '0', 10);
      const allow = parseInt(sp.dataset.allowance || '0', 10);
      if (chosen > allow) setTimeout(enforceSampleAllowance, 40);
    }).observe(host, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-chosen', 'data-allowance'],
    });
  });
}
attachSampleObservers();
document.addEventListener('cart:update', attachSampleObservers);
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', attachSampleObservers);
}
